<style scoped lang="scss">
.avatar {
	width: 48px;
	height: 48px;
	border-radius: 50%;
	object-fit: cover;
}

.status-badge {
	min-width: 70px;
	text-align: center;
}

.badge {
	margin-right: 0.25rem;
}

.bg-warrant {
	background-color: #6c757d;
}

.bg-drone {
	background-color: #0dcaf0;
}

/* Override Bootstrap hover if needed */
.table-hover tbody tr:hover {
	cursor: pointer;
}
</style>
<template>
	<div class="container-fluid p-4">
		<div class="d-flex justify-content-between align-items-center mb-4">
			<h1 class="mb-0">User Management</h1>
			<RouterLink to="/_/configuration/user/add">
				<button class="btn btn-primary" id="addUserBtn">
					<i class="bi bi-plus-circle me-2"></i>Add New User
				</button>
			</RouterLink>
		</div>

		<div class="card">
			<div class="card-body">
				<div class="input-group mb-3">
					<span class="input-group-text"><i class="bi bi-search"></i></span>
					<input
						v-model="search"
						type="search"
						class="form-control"
						placeholder="Search by name, username or district"
						aria-label="Search users"
					/>
				</div>
				<div v-if="!users">
					<p>loading...</p>
				</div>
				<div v-else-if="rows.length === 0" class="text-muted">
					No users match “{{ search }}”.
				</div>
				<div v-else class="table-responsive">
					<table class="table table-striped table-hover">
						<thead class="table-light">
							<tr>
								<th>User</th>
								<th>Organization</th>
								<th>Role</th>
								<th>Certification</th>
								<th>Tags</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="user in rows" :key="user.id">
								<td>
									<div class="d-flex align-items-center">
										<i v-if="!user.avatar" class="bi bi-avatar avatar"></i>
										<img
											:src="user.avatar"
											:alt="user.display_name"
											class="avatar me-3"
											v-else
										/>
										<div>
											<RouterLink
												:to="`/_/configuration/user/${user.id}`"
												class="fw-bold text-decoration-none"
												:title="`Open ${user.display_name}'s employee page (id ${user.id})`"
											>
												{{ user.display_name }}
											</RouterLink>
										</div>
									</div>
								</td>
								<td>
									<span v-if="user.organization">{{ user.organization }}</span>
									<span v-else class="text-muted">—</span>
								</td>
								<td>
									<span class="badge" :class="getRoleClass(user.role)">{{
										user.role
									}}</span>
								</td>
								<td>
									<span v-if="user.certification_number">{{
										user.certification_number
									}}</span>
									<span v-else class="text-muted">—</span>
									<a
										v-if="user.certification"
										:href="user.certification"
										target="_blank"
										rel="noopener"
										class="ms-1"
										title="View certificate"
									>
										<i class="bi bi-file-earmark-text"></i>
									</a>
								</td>
								<td>
									<span
										v-for="tag in user.tags"
										:key="tag"
										class="badge"
										:class="getTagClass(tag)"
									>
										{{ tag }}
									</span>
								</td>
								<td>
									<div class="d-flex gap-1">
										<RouterLink :to="`/_/configuration/user/${user.id}`">
											<button class="btn btn-sm btn-primary" title="Edit">
												<i class="bi bi-person-x"></i>
											</button>
										</RouterLink>
										<button
											v-if="isRoot && user.id !== session.self?.id"
											class="btn btn-sm btn-warning"
											title="Impersonate"
											@click="impersonate(user.id)"
										>
											<i class="bi bi-person-badge"></i>
										</button>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { computedAsync } from "@vueuse/core";
import { useUserStore } from "@/store/user";
import { useSessionStore } from "@/store/session";
import { apiClient } from "@/client";
import { User } from "@/type/api";

interface URLConfiguration {
	userAdd: string;
}

// Props (if needed from parent component)
// const props = defineProps<{
//   urlConfiguration?: URLConfiguration
// }>()

// Reactive state
const userStore = useUserStore();
const session = useSessionStore();
const isRoot = computed(() => session.self?.role === "root");
// User administration is an organization-configuration surface, so a root
// administrator sees every district's members here; everyone else sees their
// own district. The flag is reactive so the list follows the session loading.
const users = computedAsync(async (): Promise<User[]> => {
	return await userStore.withAll(isRoot.value);
});

const search = ref("");

/** The first name, for the default ordering: the display name's first token. */
function firstName(displayName: string): string {
	return displayName.trim().split(/\s+/)[0] ?? "";
}

// The list reads alphabetically by first name, narrowed by the search box.
// Neither needs a server round trip — the page already holds every user it may
// show — so the order and the filter are view state.
const rows = computed<User[]>(() => {
	const needle = search.value.trim().toLowerCase();
	const all = users.value ?? [];
	const filtered = needle
		? all.filter((u) =>
				`${u.display_name} ${u.username} ${u.organization ?? ""}`
					.toLowerCase()
					.includes(needle),
			)
		: all;
	return [...filtered].sort(
		(a, b) =>
			firstName(a.display_name).localeCompare(firstName(b.display_name)) ||
			a.display_name.localeCompare(b.display_name),
	);
});
const urlConfiguration = ref<URLConfiguration>({
	userAdd: "/configuration/user/add", // Update with your actual route
});

const getTagClass = (tag: string): string => {
	if (tag === "warrant service") return "bg-warrant";
	if (tag === "drone pilot") return "bg-drone";
	return "bg-secondary";
};

const getRoleClass = (role: string): string => {
	if (role === "root") return "bg-danger";
	if (role === "staff") return "bg-warning text-dark";
	if (role === "account-owner") return "bg-primary";
	if (role === "vision-reviewer") return "bg-info text-dark";
	return "bg-secondary";
};

async function impersonate(userId: number) {
	try {
		await apiClient.JSONPost("/api/impersonation", { id: userId });
		window.location.href = "/";
	} catch (e: any) {
		console.error("Impersonation failed:", e);
	}
}

const deactivateUser = (userId: number): void => {
	if (users.value == null) {
		return;
	}
	const user = users.value.find((u) => u.id === userId);
	if (!user) {
		return;
	}
	user.is_active = false;
	// Add your deactivation logic here (e.g., API call)
	console.log(`Deactivating user: ${userId}`);
};

// Lifecycle hooks
onMounted(() => {
	userStore.withAll(isRoot.value).catch(() => {
		// The computed list surfaces the error state; this only warms the cache.
	});
});

// Optional: API call example
// const fetchUsers = async (): Promise<void> => {
//   try {
//     const response = await fetch('/api/users')
//     const data = await response.json()
//     users.value = data
//   } catch (error) {
//     console.error('Error fetching users:', error)
//   }
// }
</script>
